import type { PropsWithChildren } from 'react';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { normalizeRole } from '@/auth/permissions';
import { getSession } from '@/lib/session';
import { toast } from '@/lib/toast';
import { canAccessPrices } from '@/auth/consultaIdePricesAccess';

function isAllowedForExternalReadOnly(pathname: string, email?: string | null, role?: string | null): boolean {
  if (normalizeRole(role) === 'CONSULTOR_SAI') {
    return pathname === '/soporte/sai' || pathname.startsWith('/soporte/sai/');
  }
  if (normalizeRole(role) === 'CLIENTE') {
    return pathname === '/cliente' || pathname.startsWith('/cliente/');
  }
  if (pathname === '/inventario' || pathname.startsWith('/inventario/')) return true;
  if ((pathname === '/precios' || pathname.startsWith('/precios/')) && canAccessPrices(email, role)) return true;
  if (normalizeRole(role) === 'PROVEEDOR' && (pathname === '/proveedor/agenda' || pathname.startsWith('/proveedor/agenda/'))) return true;
  return false;
}

function ConsultaBlockedRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;
    toast('No tienes permisos', 'warning');
    const session = getSession();
    const role = normalizeRole(session?.user?.role);
    const target = role === 'CLIENTE' ? '/cliente' : role === 'CONSULTOR_SAI' ? '/soporte/sai' : '/inventario';
    navigate(target, { replace: true, state: { from: location } });
  }, [location, navigate]);

  return null;
}

export default function ConsultaInventoryOnly({ children }: PropsWithChildren) {
  const location = useLocation();
  const session = getSession();
  const user = session?.user;
  const role = normalizeRole(user?.role);

  if (
    (role === 'CLIENTE' || role === 'CONSULTA' || role === 'CONSULTOR_SAI' || role === 'PROVEEDOR') &&
    !isAllowedForExternalReadOnly(location.pathname, user?.email, user?.role)
  ) {
    return <ConsultaBlockedRedirect />;
  }

  return children;
}
